import { useCallback, useEffect, useState } from "react";
import { RefreshCw, Database, HardDrive, Download, ShieldCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const fmtBytes = (n) => {
    if (!n && n !== 0) return "—";
    if (n < 1024) return `${n} o`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} Ko`;
    if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} Mo`;
    return `${(n / (1024 * 1024 * 1024)).toFixed(2)} Go`;
};

const filenameFromHeaders = (headers, fallback) => {
    const cd = headers?.["content-disposition"] || "";
    const m = /filename="?([^"]+)"?/.exec(cd);
    return m ? m[1] : fallback;
};

export const BackupPanel = () => {
    const { http } = useAuth();
    const [dbManifest, setDbManifest] = useState(null);
    const [filesManifest, setFilesManifest] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [downloading, setDownloading] = useState("");

    const load = useCallback(async () => {
        setLoading(true); setError("");
        try {
            const [db, files] = await Promise.all([
                http.get("/admin/backup/database/manifest"),
                http.get("/admin/backup/files/manifest"),
            ]);
            setDbManifest(db.data);
            setFilesManifest(files.data);
        } catch (e) {
            setError(e?.response?.data?.detail || "Erreur de chargement des manifestes de sauvegarde");
        } finally {
            setLoading(false);
        }
    }, [http]);

    useEffect(() => { load(); }, [load]);

    const download = async (key, path, fallbackName) => {
        setDownloading(key); setError("");
        try {
            const res = await http.get(path, { responseType: "blob" });
            const name = filenameFromHeaders(res.headers, fallbackName);
            const url = window.URL.createObjectURL(res.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = name;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (e) {
            let msg = "Échec du téléchargement";
            if (e?.response?.data instanceof Blob) {
                try { msg = JSON.parse(await e.response.data.text())?.detail || msg; } catch (_) { /* noop */ }
            } else {
                msg = e?.response?.data?.detail || msg;
            }
            setError(msg);
        } finally {
            setDownloading("");
        }
    };

    return (
        <div data-testid="backup-panel">
            <div className="flex items-start md:items-center justify-between mb-6 gap-3 flex-col md:flex-row">
                <div className="hidden md:block">
                    <div className="tech-stamp mb-1">Comptabilité · Sauvegardes</div>
                    <h1 className="font-display font-bold uppercase text-3xl tracking-tight flex items-center gap-2">
                        <ShieldCheck className="w-7 h-7 text-[#2f4f7f]" /> Sauvegardes
                    </h1>
                </div>
                <button onClick={load} data-testid="backup-refresh" className="btn-secondary !py-2 !px-3 !text-xs">
                    <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
                </button>
            </div>

            <p className="text-xs text-[#4b5d7a] mb-4 max-w-3xl">
                Téléchargez une copie complète de vos données <strong>avant toute migration</strong>.
                Tout est en lecture seule : aucun téléchargement ne modifie ni ne supprime de données.
                Conservez ces fichiers <strong>hors du conteneur</strong> (le disque est réinitialisé à chaque redéploiement).
            </p>

            {error && <div className="mb-4 p-4 border border-red-400 bg-red-50 text-red-800 text-sm" data-testid="backup-error">{error}</div>}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                {/* ---------- Base de données ---------- */}
                <div className="border border-[#dde5f0] bg-white p-5">
                    <div className="flex items-center gap-2 mb-1">
                        <Database className="w-5 h-5 text-[#2f4f7f]" />
                        <h2 className="font-display font-bold uppercase text-lg tracking-tight">Base de données</h2>
                    </div>
                    <p className="text-xs text-[#4b5d7a] mb-4">
                        Dump restaurable (<code>mongorestore --gzip --dir …</code>), incluant les fichiers GridFS
                        (reçus de dépenses, pièces jointes de soumissions).
                    </p>

                    <div className="flex items-center gap-6 mb-4 text-sm">
                        <div>
                            <div className="tech-stamp text-[#4b5d7a]">Collections</div>
                            <div className="font-mono text-2xl font-bold" data-testid="backup-collection-count">
                                {dbManifest?.collection_count ?? "—"}
                            </div>
                        </div>
                        <div>
                            <div className="tech-stamp text-[#4b5d7a]">Documents</div>
                            <div className="font-mono text-2xl font-bold" data-testid="backup-total-documents">
                                {dbManifest?.total_documents ?? "—"}
                            </div>
                        </div>
                    </div>

                    <button
                        onClick={() => download("dump", "/admin/backup/mongodump", "portech-dump.zip")}
                        disabled={downloading === "dump"}
                        data-testid="backup-download-dump"
                        className="btn-primary !py-2.5 !text-sm w-full flex items-center justify-center gap-2 disabled:opacity-60">
                        {downloading === "dump"
                            ? <><RefreshCw className="w-4 h-4 animate-spin" /> Préparation du dump…</>
                            : <><Download className="w-4 h-4" /> Télécharger le dump de la base</>}
                    </button>

                    <div className="mt-4 border-t border-[#eef2f8] pt-3 max-h-64 overflow-y-auto">
                        {(dbManifest?.collections || []).map((c) => (
                            <div key={c.collection} className="flex justify-between text-xs py-1 border-b border-[#f3f6fb] last:border-b-0"
                                data-testid={`backup-collection-${c.collection}`}>
                                <span className="text-[#4b5d7a] font-mono truncate">{c.collection}</span>
                                <span className="font-mono font-semibold">{c.count}</span>
                            </div>
                        ))}
                    </div>
                </div>

                {/* ---------- Fichiers disque ---------- */}
                <div className="border border-[#dde5f0] bg-white p-5">
                    <div className="flex items-center gap-2 mb-1">
                        <HardDrive className="w-5 h-5 text-[#2f4f7f]" />
                        <h2 className="font-display font-bold uppercase text-lg tracking-tight">Fichiers du disque</h2>
                    </div>
                    <p className="text-xs text-[#4b5d7a] mb-4">
                        Archive ZIP des fichiers stockés sur le disque du conteneur (reçus legacy, photos de rapports, uploads de coupes).
                    </p>

                    <div className="flex items-center gap-6 mb-4 text-sm">
                        <div>
                            <div className="tech-stamp text-[#4b5d7a]">Fichiers</div>
                            <div className="font-mono text-2xl font-bold" data-testid="backup-total-files">
                                {filesManifest?.total_files ?? "—"}
                            </div>
                        </div>
                        <div>
                            <div className="tech-stamp text-[#4b5d7a]">Taille totale</div>
                            <div className="font-mono text-2xl font-bold" data-testid="backup-total-bytes">
                                {fmtBytes(filesManifest?.total_bytes)}
                            </div>
                        </div>
                    </div>

                    <button
                        onClick={() => download("files", "/admin/backup/files", "portech-fichiers-disque.zip")}
                        disabled={downloading === "files" || (filesManifest && filesManifest.total_files === 0)}
                        data-testid="backup-download-files"
                        className="btn-primary !py-2.5 !text-sm w-full flex items-center justify-center gap-2 disabled:opacity-60">
                        {downloading === "files"
                            ? <><RefreshCw className="w-4 h-4 animate-spin" /> Préparation du ZIP…</>
                            : <><Download className="w-4 h-4" /> Télécharger les fichiers (ZIP)</>}
                    </button>
                    {filesManifest && filesManifest.total_files === 0 && (
                        <p className="text-[11px] text-[#4b5d7a] mt-2">Aucun fichier sur le disque (données persistées dans la base).</p>
                    )}

                    <div className="mt-4 border-t border-[#eef2f8] pt-3 max-h-64 overflow-y-auto">
                        {(filesManifest?.directories || []).map((d) => (
                            <div key={d.dir} className="flex justify-between text-xs py-1 border-b border-[#f3f6fb] last:border-b-0"
                                data-testid={`backup-dir-${d.dir}`}>
                                <span className="text-[#4b5d7a] font-mono truncate">{d.dir}{d.exists ? "" : " (absent)"}</span>
                                <span className="font-mono font-semibold">{d.file_count}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};
